import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Home, LogOut, UserRound } from "lucide-react";
import { roleLabel, searchPeople, useHasRole, useSession, DOMAINS } from "@vixio/data";
import { AppShell, Button, CommandPalette, Menu, OrgSwitcher, PageContextProvider, SearchButton, ThemeMenu, type Command } from "@vixio/ui";
import { useAuth } from "../auth/AuthProvider";
import { env } from "../env";
import { menuItems, navGroups } from "./registry";
import { useActiveModules } from "./useActiveModules";
import { Shortcuts } from "./Shortcuts";

export function Layout({ children }: { children: ReactNode }) {
  const session = useSession();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const mods = useActiveModules();
  const [cmdOpen, setCmdOpen] = useState(false);
  const canSearchPeople = useHasRole("member");

  const groups = useMemo(() => {
    const g = navGroups(mods, session.org, session.isPlatformAdmin);
    return [{ key: "home", label: "", items: [{ to: "/", label: "Início", icon: Home, end: true }] }, ...g];
  }, [mods, session.org, session.isPlatformAdmin]);

  // Área do menu da tela atual (aparece acima do título das telas sem trilha).
  const location = useLocation();
  const page = useMemo(() => {
    const items = menuItems(mods, session.org, session.isPlatformAdmin);
    const p = location.pathname;
    const hit = items
      .filter((i) => p === i.path || p.startsWith(`${i.path}/`))
      .sort((a, b) => b.path.length - a.path.length)[0];
    return {
      section: hit ? DOMAINS.find((d) => d.key === hit.domain)?.menu : undefined,
      label: p === "/" ? "Início" : p === "/perfil" ? "Meu perfil" : hit?.label,
    };
  }, [mods, session.org, session.isPlatformAdmin, location.pathname]);

  // Título da aba do navegador: a tela atual e a organização (várias abas abertas ficam distinguíveis).
  const orgName = session.org?.name;
  useEffect(() => {
    document.title = [page.label, orgName, "Vixio OS"].filter(Boolean).join(" · ");
    return () => {
      document.title = "Vixio OS";
    };
  }, [page.label, orgName]);

  const commands = useMemo<Command[]>(() => {
    const domainLabel = new Map(DOMAINS.map((d) => [d.key, d.menu]));
    const items = menuItems(mods, session.org, session.isPlatformAdmin);
    return [
      { id: "nav-home", label: "Início", group: "Ir para", icon: Home, run: () => navigate("/") },
      ...items.map((i) => ({
        id: `nav-${i.path}`,
        label: i.label,
        group: "Ir para",
        hint: domainLabel.get(i.domain),
        keywords: i.keywords,
        icon: i.icon,
        run: () => navigate(i.path),
      })),
      { id: "nav-profile", label: "Meu perfil e 2FA", group: "Conta", icon: UserRound, run: () => navigate("/perfil") },
      ...session.orgs
        .filter((o) => o.id !== session.org?.id)
        .map((o) => ({ id: `org-${o.id}`, label: `Trocar para ${o.name}`, group: "Organização", run: () => session.switchOrg(o.id) })),
    ];
  }, [mods, session, navigate]);

  const orgId = session.org!.id;
  const peopleSearch = useCallback(
    async (text: string): Promise<Command[]> => {
      if (!canSearchPeople) return [];
      const rows = await searchPeople(orgId, text, 6);
      return rows.map((p) => ({
        id: `person-${p.id}`,
        label: p.full_name,
        hint: p.email ?? p.company_name ?? undefined,
        group: "Pessoas",
        icon: UserRound,
        run: () => navigate(`/pessoas/${p.id}`),
      }));
    },
    [orgId, canSearchPeople, navigate],
  );

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen((o) => !o);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <AppShell
      groups={groups}
      sidebarFooter={
        <>
          <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={session.email ?? ""}>
            {session.email}
          </div>
          {env.envLabel ? <div className="mono">ambiente: {env.envLabel}</div> : null}
        </>
      }
      topbar={
        <>
          <OrgSwitcher
            orgs={session.orgs.map((o) => ({ id: o.id, name: o.name, roleLabel: roleLabel(o.role) }))}
            currentId={session.org?.id ?? null}
            onSwitch={(id) => {
              session.switchOrg(id);
              navigate("/");
            }}
            footer={session.isPlatformAdmin ? { label: "Gerenciar organizações", onSelect: () => navigate("/organizacoes") } : undefined}
          />
          <span className="vx-topbar__spacer" />
          <SearchButton onOpen={() => setCmdOpen(true)} />
          {mods.map((m) => (m.topbar ? <m.topbar key={`topbar-${m.key}`} /> : null))}
          <ThemeMenu />
          <Menu
            label={session.email ?? "Conta"}
            trigger={<Button variant="ghost" iconOnly label="Conta" icon={<UserRound />} />}
            items={[
              { label: "Meu perfil e 2FA", icon: <UserRound aria-hidden="true" />, onSelect: () => navigate("/perfil") },
              {
                label: "Sair",
                icon: <LogOut aria-hidden="true" />,
                separatorBefore: true,
                onSelect: () => {
                  void signOut().then(() => navigate("/entrar"));
                },
              },
            ]}
          />
          <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} commands={commands} search={peopleSearch} />
          <Shortcuts />
        </>
      }
    >
      <PageContextProvider value={page}>{children}</PageContextProvider>
    </AppShell>
  );
}
